import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { Util } from '@libs/utilities/util';
import { ColorCodeName } from '@libs/libraries/shared';

import { NgRgbColorPickerComponent } from '../rgb-color-picker/rgb-color-picker.component';
import { NgAbstractInputComponent } from '../../shared/inputs.abstract';


@Component({
    selector: 'ng-rgb-named-color-picker',
    templateUrl: './rgb-named-color-picker.component.html',
    styleUrls: ['./rgb-named-color-picker.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: NgRgbNamedColorPickerComponent,
            multi: true
        }
    ],
    standalone: false
})
export class NgRgbNamedColorPickerComponent extends NgAbstractInputComponent<string> implements OnInit {

	//** Configuration */
	private readonly DEFAULT_HEX_COLOR: string = '#000000';

	@ViewChild('rgbColorPicker') rgbColorPicker!: NgRgbColorPickerComponent;

	//** Input / Output Decorators */
	@Input() isDarkMode			: boolean			= false;
	@Input() defaultColor 	 	: string 		 	= null!;
	@Input() colorOptionList 	: Array<string[]> 	= null!;
	@Input() resetValue 			: string 			= null!;
	@Input() resetValueName 		: string 			= 'No Color';
	@Input() resetPreviewColor 	: string 			= '#ffffff';

	//** Helper Variables */
	colorData 	 				: INgColorData 		= null!;
	predefinedColorsList		: INgColorData[] 	= [];
	newColor 					: string 			= '';
	isPreview					: boolean			= false;
	private previewOriginalValue: string | null = null;

	constructor(
		private colorCodeName 	: ColorCodeName
	) {
		super('rgb-named-color-picker');
	}


	/**------------------------------------------------------
	 * Live Cycle Hooks
	 */
	ngOnInit(): void {

		// check the input parameters
		if (Util.Array.isEmpty(this.colorOptionList)) throw new Error(`RgbNamedColorPickerComponent => ngOnInit => FATAL ERROR: the provided colorOptionList is empty (a color list is required of the following format: "[['#000000', 'Black'], ['#424242', 'Dark Grey'], ...]")`);
	}


	/**------------------------------------------------------
	 * Control Value Accessor Function
	 */
	override writeValue(value: string): void {

		// A form can echo a hover preview back through ngModel. Keep the committed
		// color so leaving the menu restores it rather than saving the preview.
		if (this.isPreview && value === this.newColor) return;
		this.isPreview = false;
		this.previewOriginalValue = null;

		//0 - update color value
		this.value = value || this.defaultColor || this.DEFAULT_HEX_COLOR;

		//1 - check the color is passed into the component and check the color is valid or not
		if (!Util.Color.isHexColor(this.value) && !this.isResetValue(this.value)) throw new Error(`RgbNamedColorPickerComponent => writeValue => FATAL ERROR: colorValue of "${this.value}" is not valid hex color`);
		this.newColor = this.getPickerColor(this.value);

		//2 - update the ui data
		this.updateUiData();
	}


	/**------------------------------------------------------
	 * Save color
	 */
	previewColor(hexCode: string): void {

		//0 - save the color and emit the color
		this.newColor = hexCode;
		this.onChange(hexCode);

		//1 - update the ui data
		this.updateUiData();
	}

	applyColor(hexCode: string): void {

		//0 - save the color and emit the color
		this.isPreview = false;
		this.previewOriginalValue = null;
		this.value = hexCode;
		this.newColor = this.getPickerColor(hexCode);
		this.onChange(hexCode);

		//1 - update the ui data
		this.updateUiData();
	}

	onPickerColorChange(hexCode: string): void {

		if (this.isPreview) this.previewColor(hexCode);
		else this.applyColor(hexCode);
	}


	/**------------------------------------------------------
	 * Mouse OVer/Out
	 */
	onMouseOver(hexCode: string | null): void {

		// save the color and emit the new color
		if (!hexCode) return;
		if (!this.isPreview) this.previewOriginalValue = this.value;
		this.isPreview 	= true;
		this.previewColor(hexCode);
	}

	onMouseOut(hexCode: string | null): void {

		// save the new color and emit the new color
		if (!hexCode || !this.isPreview) return;
		const originalValue = this.previewOriginalValue ?? hexCode;
		this.isPreview 	= false;
		this.previewOriginalValue = null;
		this.newColor 	= this.getPickerColor(originalValue);
		this.onChange(originalValue);
		this.updateUiData();
	}

	//** Reset function */
	onResetColor(): void {

		// set default color and check the color dark or light
		const resetColor: string = this.resetValue || this.defaultColor || this.DEFAULT_HEX_COLOR;

		this.isPreview = false;
		this.previewOriginalValue = null;
		this.value = resetColor;
		this.newColor = this.getPickerColor(resetColor);
		this.rgbColorPicker.isDarkColor = this.rgbColorPicker.checkIsDarkColor(this.newColor);
		this.onChange(resetColor);
		this.updateUiData();
	}


	/**------------------------------------------------------
	 * Helper Functions
	 */
	private updateUiData() {

		//0 - create color data object by color value passed as input
		this.generateColorValueData(this.value!);

		//1 - create color list data object by color value list as input
		if (this.colorOptionList) this.updatePredefinedColorListData();
	}

	//** Generate passed color as data object */
	private generateColorValueData(hexCode: string) {
		if (this.isResetValue(hexCode)) {
			this.colorData = {
				name 				: this.resetValueName,
				hexCode 			: '',
				isPredefinedColor 	: false,
				isCurrentlySelected : true
			};
			return;
		}

		//0 - create color data object
		const colorData: INgColorData = {
			name 				: this.getColorNameByHexCode(hexCode),
			hexCode 			: hexCode,
			isPredefinedColor 	: this.colorOptionList ? this.isPredefinedColor(hexCode) : false,
			isCurrentlySelected : hexCode === this.newColor
		};

		//1 - save the color data object
		this.colorData = colorData;
	}

	//** Gets the color name by using HEX code of color */
	private getColorNameByHexCode(hexCode: string): string {

		//0 - checks the current hex code present in the default defined colors
		if (this.colorOptionList) {
			const predefinedColors: string[] = this.colorOptionList.find((color: string[]) => color[0] === hexCode)!;
			if (!Util.Array.isEmpty(predefinedColors)) return Util.String.titleCase(predefinedColors[1]);
		}

		//1 - if the color not present in the default defined colors then gets the color from color conversion service
		const colorName: string = this.colorCodeName.getColorNamePredefined(hexCode).name;
		return colorName;
	}

	//** Check the color is existing the default color or not */
	private isPredefinedColor(hexCode: string): boolean {

		//0 - check the selected color in the default color list
		const isPreDefinedColor: boolean = this.colorOptionList.some((colorData: string[]) => colorData[0].toLowerCase() === hexCode.toLowerCase());

		//1 - if existing in the default color
		return isPreDefinedColor;
	}

	//** Create color list data object */
	private updatePredefinedColorListData() {

		//0 - clear the predefinedList
		if (Util.Array.isEmpty(this.predefinedColorsList)) {
			this.generatePredefinedColorListData();
			return;
		}

		//1 - iterate over the passed color list and create color data object
		for (const colorList of this.predefinedColorsList) {
			colorList.isCurrentlySelected = colorList.hexCode === this.value;
		}
	}

	private generatePredefinedColorListData() {
		for (const color of this.colorOptionList) {
			const colorData: INgColorData = {
				name 				: color[1],
				hexCode 			: color[0],
				isPredefinedColor 	: true,
				isCurrentlySelected : color[0] === this.newColor
			};
			this.predefinedColorsList.push(colorData);
		}
	}

	private isResetValue(value: string): boolean {
		return Boolean(this.resetValue) && value === this.resetValue;
	}

	private getPickerColor(value: string): string {
		if (Util.Color.isHexColor(value)) return value;
		if (this.isResetValue(value) && Util.Color.isHexColor(this.resetPreviewColor)) return this.resetPreviewColor;
		if (Util.Color.isHexColor(this.defaultColor)) return this.defaultColor;
		return this.DEFAULT_HEX_COLOR;
	}
}


//** Interfaces --------------------------------- */
interface INgColorData {
	name 				: string;
	hexCode 			: string;
	isPredefinedColor 	: boolean;
	isCurrentlySelected : boolean;
}
